import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { Link, withRouter } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { addStudentSuccess } from '../../actions/action_parent';

const MainHeader = styled.h4`
  margin: 0;
  font-size: 24px;
`;

const Divider = styled.hr`
  margin: 12px 0 20px 0;
  border-top: 1px solid #ddd;
`;

const Wrapper = styled.div`
  margin-top: 0;
  background: #fff;
  border-radius: 4px;
  border: 1px solid #ccc;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
  padding: 24px 24px 24px;
  margin-bottom: 32px;
`;

const CancelLink = styled(Link)`
  display: inline-block;
  margin-left: 8px;
`;

const SkeletonSelect = styled.div`
  width: 100%;
  height: 38px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const ParentAccountAddStudent = ({ history }) => {
  const onSuccessRoute = '/my-account';
  const dispatch = useDispatch();

  const { publicToken } = useSelector((state) => state.parent);
  const [showRelationship, setShowRelationship] = useState(false);
  const [relationshipOptions, setRelationshipOptions] = useState([]);
  const [relationshipLoading, setRelationshipLoading] = useState(true);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      guardianRelationship: '',
    },
  });

  const [firstNameSubmissionError, setFirstNameSubmissionError] = useState('');
  const [lastNameSubmissionError, setLastNameSubmissionError] = useState('');

  useEffect(() => {
    // Fetch relationship options from API
    const fetchRelationshipOptions = async () => {
      try {
        const response = await axios.get(
          '/api/v2/students/guardian_relationship_options',
          {
            headers: { publicToken },
          },
        );

        if (response.data.showRelationship) {
          setShowRelationship(true);
          // Convert API options to dropdown format
          const options = [
            { value: '', label: '- Select Relationship -' },
            ...Object.entries(response.data.options).map(([value, label]) => ({
              value,
              label,
            })),
          ];
          setRelationshipOptions(options);
        }
      } catch (error) {
        console.error('Failed to fetch relationship options:', error);
      } finally {
        setRelationshipLoading(false);
      }
    };

    fetchRelationshipOptions();
  }, [publicToken]);

  const onSubmit = async (data) => {
    try {
      const requestData = {
        first_name: data.firstName,
        last_name: data.lastName,
        publicToken,
      };

      // Only include guardian_relationship if shown and not empty
      if (showRelationship && data.guardianRelationship) {
        requestData.guardian_relationship = data.guardianRelationship;
      }

      const response = await axios.post('/api/v2/students', requestData);
      dispatch(addStudentSuccess(response.data));
      history.push(onSuccessRoute);
    } catch (error) {
      console.log('error', error);

      let firstNameErrorMessages = '';
      if (error.response.data.first_name) {
        error.response.data.first_name.forEach((message) => {
          firstNameErrorMessages += message;
        });
      }
      setFirstNameSubmissionError(firstNameErrorMessages);

      let lastNameErrorMessages = '';
      if (error.response.data.last_name) {
        error.response.data.last_name.forEach((message) => {
          lastNameErrorMessages += message;
        });
      }
      setLastNameSubmissionError(lastNameErrorMessages);
    }
  };

  return (
    <div className="bnl-dashboard-container">
      <div className="bnl-dashboard">
        <MainHeader>Add Student</MainHeader>
        <Divider />
        <div className="order-checkout__main-content row">
          <div className="order-checkout__left col-xs-12 col-md-8 col-lg-8">
            <Wrapper>
              <form onSubmit={handleSubmit((data) => onSubmit(data))}>
                <div className="form-group">
                  <label htmlFor="firstName">First name</label>
                  <input
                    type="text"
                    className={`form-control ${
                      errors?.firstName ? 'is-invalid' : ''
                    }`}
                    name="firstName"
                    {...register('firstName', {
                      required: 'First name is required',
                    })}
                  />
                  {errors.firstName && (
                    <div className="text-danger">
                      {errors?.firstName.message}
                    </div>
                  )}
                  {firstNameSubmissionError && (
                    <div className="text-danger">{`First name ${firstNameSubmissionError}`}</div>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="lastName">Last name</label>
                  <input
                    type="text"
                    className={`form-control ${
                      errors?.lastName ? 'is-invalid' : ''
                    }`}
                    name="lastName"
                    id="lastName"
                    {...register('lastName', {
                      required: 'Last name is required',
                    })}
                  />
                  {errors.lastName && (
                    <div className="text-danger">
                      {errors?.lastName.message}
                    </div>
                  )}
                  {lastNameSubmissionError && (
                    <div className="text-danger">{`Last name ${lastNameSubmissionError}`}</div>
                  )}
                </div>
                {(showRelationship || relationshipLoading) && (
                  <div className="form-group">
                    <label htmlFor="guardianRelationship">
                      Your Relationship to Student
                    </label>
                    {relationshipLoading ? (
                      <SkeletonSelect />
                    ) : (
                      <>
                        <select
                          className={`form-control ${
                            errors?.guardianRelationship ? 'is-invalid' : ''
                          }`}
                          name="guardianRelationship"
                          id="guardianRelationship"
                          {...register('guardianRelationship')}
                        >
                          {relationshipOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        {errors.guardianRelationship && (
                          <div className="text-danger">
                            {errors?.guardianRelationship.message}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
                <button type="submit" className="btn btn-primary">
                  Add Student
                </button>
                <CancelLink to={onSuccessRoute} className="ml-2">
                  Cancel
                </CancelLink>
              </form>
            </Wrapper>
          </div>
        </div>
      </div>
    </div>
  );
};

export default withRouter(ParentAccountAddStudent);
